import { useQuery } from '@tanstack/react-query';

import { kubeSoloConfigInspect } from '@api/sdk.gen';
import type { KubesoloConfigResponse } from '@api/types.gen';

import { EnvironmentId } from '@/react/portainer/environments/types';
import { withError } from '@/react-tools/react-query';
import { isAxiosError } from '@/react/portainer/services/axios/utils/isAxiosError';

const SNAPSHOT_POLL_INTERVAL_MS = 5000;

const queryKeys = {
  config: (environmentId: EnvironmentId) =>
    ['kubesolo', environmentId, 'config'] as const,
};

export function useKubeSoloConfig(
  environmentId: EnvironmentId,
  { enabled }: { enabled?: boolean } = {}
) {
  return useQuery(
    queryKeys.config(environmentId),
    () => getKubeSoloConfig(environmentId),
    {
      ...withError("Unable to read KubeSolo's configuration"),
      enabled,
      // The agent reads KubeSolo on every request, so only when the page opens or on Refresh.
      staleTime: Infinity,
      refetchInterval: (config) =>
        config === null ? SNAPSHOT_POLL_INTERVAL_MS : false,
    }
  );
}

/** KubeSolo's version and configuration from the latest snapshot, or null before a snapshot has carried them. */
async function getKubeSoloConfig(
  environmentId: EnvironmentId
): Promise<KubesoloConfigResponse | null> {
  try {
    const { data } = await kubeSoloConfigInspect({
      path: { id: environmentId },
    });
    return data;
  } catch (error) {
    // a new environment's first snapshot is a state the page waits for, not a failure to notify about
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }

    throw error;
  }
}
